import React, {useContext, useEffect} from 'react';
import {observer} from 'mobx-react-lite';
import {
    IButtonProps,
    IChartDataProps,
    IContentFiltersProps,
    IFilterItemProps,
    IFilterProps,
    IPageSelectProps,
    IPageTitleAndFiltersProps,
    IQuestionProps,
    ITemplateDashboardFeedbackProps,
    ITemplateDashboardQuestionsProps,
    ITemplateGeneralCardProps,
    ITemplateGeneralRowProps,
    ITemplateOverviewCardProps,
    ITemplatePlainTextCardProps,
    Main,
    ModalProvider,
    Notification,
    TemplateDashboard,
    TFilterValue,
} from '../../../patterns';
import {StoreContext} from '../base/root-store';
import {
    AnswerWithComment,
    AnswerWithCommentOnQuestionCard,
    DashboardData,
    DashboardPageTab,
    QuestionCard,
} from './team-results-store';
import {IFeedbackItemProps} from '../../../patterns/components/feedback/feedback';
import {formatDateRange, formatToDayAndMonth, periodIdFromRange, rangeFromPeriodId} from '../base/dateUtils';
import {WebappBase} from './WebappBase';
import {IAnswerProps} from '../../../patterns/components/feedback-answers/feedback-answers';
import {MyResultsStore} from './my-results-store';
import {ScoreTooltip} from '../base/ScoreTooltip';
import {UserStore} from '../user/user-store';
import {MyCategoryHelpModal} from './MyCategoryHelpModal';
import {MyFeedbackHelpModal} from './MyFeedbackHelpModal';
import {MyOverviewHelpModal} from './MyOverviewHelpModal';
import {ISelectDatepickerProps} from '../../../patterns/components/forms/select/select';
import {DateRange} from 'react-day-picker';
import {getCategoryName, isCustomCategory} from '../categories/CategoriesUtil';

export const MyDashboardPage = observer((): JSX.Element => {
    const store = useContext(StoreContext).myResultsStore;
    const userStore = useContext(StoreContext).userStore;

    useEffect(() => {
        store.onEnter();
    }, []);

    if (store.state === 'error' && store.errorReason === 'user-deleted') {
        return <Main showBackground={true}>
            <Notification
                text={store.label('common.user.deleted.error-message')}
                defaultIsOpen={true}
                type="info"
            />
        </Main>;
    }

    return (
        <WebappBase
            store={store}
            title={store.label('dashboard.title')}
            renderChildren={(): React.ReactNode => (
                <React.Fragment>
                    <ModalProvider
                        isOpen={store.variables.openModal !== undefined}
                        onToggle={(): void => store.closeModal()}>
                        {store.variables.openModal === 'help-my-overview' && <MyOverviewHelpModal/>}
                        {store.variables.openModal === 'help-my-feedback' && <MyFeedbackHelpModal/>}
                        {store.variables.openModal === 'help-my-category' && <MyCategoryHelpModal/>}
                    </ModalProvider>
                    <TemplateDashboard
                        pageTitleAndFilters={titleAndFilters(store, userStore)}
                        history={myOverviewCardProps(store, userStore)}
                        feedback={myFeedbackProps(store, userStore)}
                        categories={myCategoryProps(store, userStore)}
                        plainText={mentorAdviceProps(store, userStore)}
                        questions={myQuestionProps(store, userStore)}
                        adHocQuestions={myAdHocQuestionProps(store, userStore)}
                        noData={noDataProps(store)}
                    />
                </React.Fragment>
            )}
        />
    );
});

const titleAndFilters = (teamStore: MyResultsStore, userStore: UserStore): IPageTitleAndFiltersProps => ({
    title: teamStore.label('dashboard.title'),
    dashboardSwitcher: pageSwitcherProps(teamStore, userStore),
    contentFilters: contentFilterProps(teamStore, userStore),
});


const pageSwitcherProps = (store: MyResultsStore, userStore: UserStore): IFilterProps | undefined => {
    const isAdminOrManager = userStore.payload?.isManagerOrPartner === true || userStore.payload?.isAdmin === true;
    const isPersonalDashBoardEnabled = userStore.payload?.isPersonalDashboardEnabled === true;
    const allPageSwitcherItems: IFilterItemProps[] = [
        {
            id: 'team-results',
            name: 'filter',
            label: store.label('dashboard.switcher.team-results'),
            value: 'team-results',
        },
        {
            id: 'new-hires',
            name: 'filter',
            label: store.label('dashboard.switcher.new-hires'),
            value: 'new-hires',
            count: userStore.payload?.peopleInOnboardingCount !== undefined && userStore.payload?.isManagerOrPartner === true ? userStore.payload?.peopleInOnboardingCount : undefined,
        },
        {
            id: 'my-results',
            name: 'filter',
            label: store.label('dashboard.switcher.my-results'),
            value: 'my-results',
            isRedBackground: true,
        },
    ];

    let eligibleSwitcherItems: IFilterItemProps[];

    if (isPersonalDashBoardEnabled && isAdminOrManager) {
        eligibleSwitcherItems = allPageSwitcherItems;
    } else if (isPersonalDashBoardEnabled && !isAdminOrManager) {
        eligibleSwitcherItems = allPageSwitcherItems.filter((item) => item.id !== 'new-hires');
    } else if (!isPersonalDashBoardEnabled && isAdminOrManager) {
        eligibleSwitcherItems = allPageSwitcherItems.filter((item) => item.id !== 'my-results');
    } else {
        eligibleSwitcherItems = [];
    }

    return eligibleSwitcherItems.length !== 0 ? ({
        label: 'Filter label',
        onChange: (value: TFilterValue): void => {
            store.onDashboardTabChange(value as DashboardPageTab);
        },
        value: store.variables.openTab,

        items: eligibleSwitcherItems,
    }) : undefined;
};

const contentFilterProps = (store: MyResultsStore, userStore: UserStore): IContentFiltersProps => ({
    filterValues: [
        {oneFilterValue: categoriesFilterProps(store, userStore)},
        {oneFilterValue: timeframeFilterProps(store)},
    ],
});

const getDefaultCategoryName = (store: MyResultsStore, catId: string | undefined): string => {
    const category = store?.categories?.find((cat) => cat.id === catId);

    return category?.categoryName ?? '';
};

const categoriesFilterProps = (store: MyResultsStore, userStore: UserStore): IPageSelectProps => {
    const filterOptions = store.payload!.filter.categories;
    const options = filterOptions.map((filterOption) => ({
        label: getCategoryName(userStore.payload!.language, filterOption.id, store.categories),
        value: filterOption.id,
    }));

    return ({
        label: store.label('dashboard.filter.categories.title'),
        id: 'dashboard-categories',
        defaultValue: filterOptions.filter((filterOption) => filterOption.selected)[0].id,
        search: {
            id: 'categories-search',
            name: 'categories',
            label: store.label('common.filter.search.label.categories'),
            focusLabel: store.label('common.filter.search.focuslabel'),
        },
        options,
        onChange: ((filterOptionsId) => store.onCategoryChange('' + filterOptionsId)),
    } as IPageSelectProps);
};

const timeframeFilterProps = (store: MyResultsStore): IPageSelectProps => {
    const filterOptions = store.payload!.filter.periods;

    const options = filterOptions.map((filterOption) => ({
        label: store.timeframeFilterLabels(filterOption),
        value: filterOption.id,
    }));

    return ({
        label: store.label('common.filter.period.title'),
        id: 'dashboard-time',
        defaultValue: store.variables.isCustomDate ? undefined : filterOptions.find((filterOption) => filterOption.selected)?.id,
        options,
        datepicker: getDatepickerProps(store),
        onChange: ((filterOptionId: string) => {
            store.setCustomDateSelected(false);
            store.onPeriodChange('' + filterOptionId);
        }),
    } as IPageSelectProps);
};

const getDatepickerProps: (store: MyResultsStore) => ISelectDatepickerProps = (store: MyResultsStore) => ({
    datepickerProps: {
        format: 'dd.MM.yyyy',
        type: 'range',
        defaultValue: {from: undefined, to: undefined} as DateRange,
        field: [
            {
                label: store.label('user-menu.datepicker.from'),
                id: 'datepicker-from',
                name: 'datepicker-from',
            },
            {
                label: store.label('user-menu.datepicker.to'),
                id: 'datepicker-to',
                name: 'datepicker-to',
            },
        ],
    },
    onChange: (data): void => {
        const range = (data as DateRange);
        const {from, to} = range;

        if (to && from) {
            store.setCustomDateSelected(true);
            store.onPeriodChange(periodIdFromRange(range));
        }
    },
    customDateDisplayValue: store.variables.isCustomDate && store.variables.periodId ? formatDateRange(rangeFromPeriodId(store.variables.periodId)) : undefined,
});

const myOverviewCardProps = (store: MyResultsStore, userStore: UserStore): ITemplateOverviewCardProps | undefined => {
    if (store.payload!.overview === undefined) {
        return undefined;
    } else {
        const chartScores = store.payload!.overview.chart.length === 1 ? [store.payload!.overview.chart[0], store.payload!.overview.chart[0]] : store.payload!.overview.chart;
        const chartData: IChartDataProps[] = chartScores.map((chartpoint, index): IChartDataProps => ({
            name: index + 1,
            value: chartpoint.value.scorePercentage,
            compare: chartpoint.compareTo.scorePercentage,
        })).filter((_, index): boolean => store.payload!.overview!.chart[index].value.answered !== 0);

        const helpButtonProps: IButtonProps[] = [
            {
                text: '',
                color: 'white',
                icon: 'info',
                onClick: (): void => store.openOverviewHelpModal(),
            },
        ];

        const categoryName = getCategoryName(userStore.payload!.language, store.payload!.tooltipPayload.categoryId, store.categories);
        const compareToName = getCategoryName(userStore.payload!.language, store.payload!.tooltipPayload.compareToCategoryId);

        return {
            title: store.label('dashboard.overview.title'),
            actions: helpButtonProps,
            chart: {
                aspect: 2.2,
                tickFormatter: (tick: number): string => formatToDayAndMonth(chartScores[tick - 1].period.to),
                id: 'chart-1',
                data: chartData,
                areas: [
                    {
                        key: 'value',
                    },
                ],
                lines: [
                    {key: 'compare', color: 'gray', strokeWidth: 2, strokePattern: '12 8'},
                ],
                showTooltip: true,
                // eslint-disable-next-line react/display-name
                tooltipFormatter: (index: number): JSX.Element => (
                    <ScoreTooltip
                        period={store.payload!.overview!.chart[index - 1].period}
                        name={categoryName}
                        compareToName={categoryName !== compareToName ? compareToName : undefined}
                        value={store.payload!.overview!.chart[index - 1].value}
                        compareTo={categoryName !== compareToName ? store.payload!.overview!.chart[index - 1].compareTo : undefined}
                    />
                ),
            },
            bars: [
                {
                    icon: 'users',
                    color: 'blue',
                    label: store.label('dashboard.overview.activity', {
                        answers_percentage: store.payload!.overview.total.participationPercentage.toString(),
                    }),
                    percent: store.payload!.overview.total.participationPercentage,
                },
                {
                    icon: 'like',
                    color: 'green',
                    label: store.label('dashboard.overview.score', {score_percentage: store.payload!.overview.total.scorePercentage.toString()}),
                    percent: store.payload!.overview.total.scorePercentage,
                },
            ],
            notEnoughDataText: store.payload!.overview.chart.length < 1 ? store.label('dashboard.not-enough-data') : undefined,
        };
    }
};

const myFeedbackProps = (store: MyResultsStore, userStore: UserStore): ITemplateDashboardFeedbackProps | undefined => {
    if (store.payload!.feedback === undefined) {
        return undefined;
    } else {
        const questions: IFeedbackItemProps[] = store.payload!.feedback.questionFeedback.map((question) => {
            const answers = question.answers.map((answer: AnswerWithComment): IAnswerProps => ({
                reactionType: answer.answer === 'null' ? 'talk-bubble' : 'thumb',
                direction: answer.answer === '100' || answer.answer === '75' ? 'up' : 'down',
                text: answer.text,
                hasSeen: answer.hasSeen ? answer.hasSeen : undefined,
            }));

            return {
                title: question.categoryId !== undefined ? getCategoryName(userStore.payload!.language, question.categoryId, store.categories) : store.label(`common.category.ad-hoc`),
                question: question.questionText,
                answers: {answers},
                count: answers.length,
                id: question.catalogQuestionId,
            } as IFeedbackItemProps;
        });

        const helpButtonProps: IButtonProps[] = [
            {
                text: '',
                color: 'white',
                icon: 'info',
                onClick: (): void => store.openFeedbackHelpModal(),
            },
        ];

        return ({
            title: store.label('dashboard.feedback.title'),
            actions: helpButtonProps,
            data: {
                items: questions.length === 0 ? {
                    type: 'info',
                    text: store.label('dashboard.not-enough-data'),
                } : questions,
            },
            more: {showExpand: true},
        });
    }
};

const myCategoryProps = (store: MyResultsStore, userStore: UserStore): ITemplateGeneralCardProps | undefined => {
    if (store.payload!.categories?.categories === undefined) {
        return undefined;
    } else {
        const categoryRows = store.payload!.categories.categories
            .filter((categoryData) => categoryData.barpoint.value.answered !== 0)
            .map((row) => dataRow(store, 'category', row, userStore));

        const helpButtonProps: IButtonProps[] = [
            {
                text: '',
                color: 'white',
                icon: 'info',
                onClick: (): void => store.openCategoryHelpModal(),
            },
        ];

        return ({
            title: store.label('dashboard.categories.title'),
            actions: helpButtonProps,
            table: {
                columns: [
                    {
                        header: store.label('dashboard.categories.header.category'),
                        width: '100%',
                        isSortable: true,
                    },
                    {
                        header: store.variables.periodId ? formatDateRange(rangeFromPeriodId(store.variables.periodId)) : '',
                        isSortable: true,
                    },
                    {
                        header: store.label('dashboard.common.header.activity'),
                        isSortable: true,
                    },
                ],
                rows: store.payload!.overview!.total.answered < 1 ? {
                    type: 'info',
                    text: store.label('dashboard.not-enough-data'),
                } : categoryRows,
            },
        });
    }
};

const mentorAdviceProps = (store: MyResultsStore, userStore: UserStore): ITemplatePlainTextCardProps | undefined => {
    if (store.payload!.overview === undefined) {
        return undefined;
    } else {
        const questions: string[] = [
            `${store.label('dashboard.personal.mentor.question-1')}`,
            `${store.label('dashboard.personal.mentor.question-2')}`,
            `${store.label('dashboard.personal.mentor.question-3')}`,
            `${store.label('dashboard.personal.mentor.question-4')}`,
            `${store.label('dashboard.personal.mentor.question-5')}`,
            `${store.label('dashboard.personal.mentor.question-6')}`];

        return (
            {
                text: {
                    rows: {
                        type: 'mentor',
                        mentorAdvice: {
                            greeting: store.label('dashboard.personal.mentor.greeting', {
                                employee: userStore.payload!.fullName.firstName,
                            }),
                            introductionText: store.label('dashboard.personal.mentor.introduction'),
                            questions,
                        },
                    },
                },
            }
        );
    }
};

const dataRow = (store: MyResultsStore, translationKey: string, data: DashboardData, userStore: UserStore): ITemplateGeneralRowProps => {
    const score = data.barpoint.value.scorePercentage;
    const compareScore = data.barpoint.compareTo.scorePercentage;
    const labelKey = getCategoryName(userStore.payload!.language, data.value, store.categories);
    const descriptionTooltip = isCustomCategory(data.value) ? labelKey : `common.${translationKey}.description.` + getDefaultCategoryName(store, data.value);
    const allValueLabelKey = getCategoryName(userStore.payload!.language, store.payload!.tooltipPayload.compareToCategoryId, store.categories);

    return ({
        id: data.value,
        title: translationKey === 'roles' || translationKey === 'tags' ? allValueLabelKey : store.label(labelKey),
        titleTooltip: translationKey === 'category' ? store.label(descriptionTooltip) : undefined,
        score: {
            progress: {
                percent: score,
                color: score >= compareScore ? 'green' : 'red',
                indicator: compareScore,
            },
            indicator: {
                number: score,
                color: score >= compareScore ? 'green' : 'red',
            },
        },
        scoreTooltip: <ScoreTooltip
            period={store.payload!.tooltipPayload.period}
            name={labelKey}
            compareToName={labelKey !== allValueLabelKey ? allValueLabelKey : undefined}
            value={data.barpoint.value}
            compareTo={data.barpoint.compareTo}
        />,
        activity: {
            percent: data.barpoint.value.participationPercentage,
            color: 'blue',
            indicator: data.barpoint.compareTo.participationPercentage,
        },
        activityTooltip: <ScoreTooltip
            period={store.payload!.tooltipPayload.period}
            name={labelKey}
            compareToName={labelKey !== allValueLabelKey ? allValueLabelKey : undefined}
            value={data.barpoint.value}
            compareTo={data.barpoint.compareTo}
        />,
        isFocused: data.focused,
    } as ITemplateGeneralRowProps);
};

const myQuestionProps = (store: MyResultsStore, userStore: UserStore): ITemplateDashboardQuestionsProps | undefined => {
    if (store.payload!.questions === undefined || store.payload!.questions.questions.length === 0) {
        return undefined;
    } else {
        const visibleQuestionCards = store.visibleQuestionCards().map((question) => questionCardProps(store, question, userStore));

        return ({
            items: visibleQuestionCards,
            button: store.shouldShowLoadMoreButton() ? {
                text: store.label('dashboard.questions.load-more'),
                onClick: (): void => store.loadMoreQuestionCards(),
            } : undefined,
        });
    }
};

const myAdHocQuestionProps = (store: MyResultsStore, userStore: UserStore): ITemplateDashboardQuestionsProps | undefined => {
    if (store.payload!.adHocQuestions === undefined || store.payload!.adHocQuestions.questions.length === 0) {
        return undefined;
    } else {
        return ({
            items: store.payload!.adHocQuestions.questions.map((question) => questionCardProps(store, question, userStore)),
        });
    }
};

const questionCardProps = (store: MyResultsStore, question: QuestionCard, userStore: UserStore): IQuestionProps => {
    const score = question.barpoint.value.scorePercentage;

    const chartScores = question.chartScores.length === 1 ? [question.chartScores[0], question.chartScores[0]] : question.chartScores;

    const answers = question.feedbackAnswers.some((feedback) => feedback.answer === null)
        ? []
        : question.feedbackAnswers.map((answer: AnswerWithCommentOnQuestionCard): IAnswerProps => ({
            reactionType: answer.answer === 'null' ? 'talk-bubble' : 'thumb',
            direction: answer.answer === '100' || answer.answer === '75' ? 'up' : 'down',
            text: answer.text,
            hasSeen: answer.hasSeen || undefined,
        }));

    return ({
        category: question.categoryId !== undefined ? getCategoryName(userStore.payload!.language, question.categoryId, store.categories) : store.label(`common.category.ad-hoc`),
        text: question.questionText,
        answers: {answers},
        bars: question.barpoint.value.onlyFreeTextAnswers
            ? []
            : [
                {
                    icon: 'like',
                    color: score > 50 ? 'green' : 'red',
                    label: store.label('dashboard.questions.score', { score_percentage: score.toString() }),
                    percent: score,
                },
            ],
        chart: {
            id: 'chart-1',
            data: chartScores.map((chartScore: number, index: number): IChartDataProps => ({
                name: index + 1,
                a: chartScore,
            })),
            areas: [
                {
                    key: 'a',
                    color: score > 50 ? 'greenPastel' : 'redPastel',
                    strokeWidth: 1,
                    lineColor: question.notEnoughDataDatapoint || question.barpoint.value.onlyFreeTextAnswers ? 'gray' : score > 50 ? 'green' : 'red',
                },
            ],
            showTooltip: false,
            aspect: 6.57,
            hideDots: true,
            hideGrid: true,
            hideXAxis: true,
            hideYAxis: true,
        },
    });
};

const noDataProps = (store: MyResultsStore): ITemplatePlainTextCardProps | undefined => {
    if (store.payload!.overview !== undefined) {
        return undefined;
    } else {
        return ({
            text: {
                rows: {
                    text: store.label('dashboard.personal.not-enough-data'),
                    type: 'info',
                },
            },
        });
    }
};
